import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TDealPipeline, TDealStatus } from "@/models/deals.model";
import { TRequestStatus } from "@/models/msc.model";

type PipelinesState = {
    initialized: boolean;
    entities: Record<number, TDealPipeline>;
    ids: number[];
    currentPipelineId: number;
    ui: {
        status: TRequestStatus;
    };
};

const initialState: PipelinesState = {
    initialized: false,
    entities: {},
    ids: [],
    currentPipelineId: 0,
    ui: {
        status: "IDLE",
    },
};

const pipelinesSlice = createSlice({
    name: "pipelines",
    initialState,
    reducers: {
        startLoading(state) {
            state.ui.status = "PENDING";
        },

        pipelinesLoaded(state, action: PayloadAction<TDealPipeline[]>) {
            state.entities = {};
            state.ids = [];

            action.payload.forEach((pipeline) => {
                state.entities[pipeline.id] = pipeline;
                state.ids.push(pipeline.id);
            });

            // if no current pipeline, use the first one
            if (!state.currentPipelineId && state.ids.length > 0) {
                state.currentPipelineId = state.ids[0];
            }

            state.initialized = true;
            state.ui.status = "IDLE";
        },

        setCurrentPipeline(state, action: PayloadAction<number>) {
            state.currentPipelineId = action.payload;
        },

        dealStatusCreated(state, action: PayloadAction<{ pipelineId: number; status: TDealStatus }>) {
            const { pipelineId, status } = action.payload;
            const pipeline = state.entities[pipelineId];
            if (pipeline) {
                pipeline.status.push(status);
            }
        },

        dealStatusUpdated(state, action: PayloadAction<{ pipelineId: number; status: TDealStatus }>) {
            const { pipelineId, status } = action.payload;
            const pipeline = state.entities[pipelineId];
            if (pipeline) {
                const index = pipeline.status.findIndex((s) => s.id === status.id);
                if (index !== -1) {
                    pipeline.status[index] = status;
                }
            }
        },

        dealStatusDeleted(state, action: PayloadAction<{ pipelineId: number; statusId: number }>) {
            const { pipelineId, statusId } = action.payload;
            const pipeline = state.entities[pipelineId];
            if (pipeline) {
                pipeline.status = pipeline.status.filter((s) => s.id !== statusId);
            }
        },

        pipelineAdded(state, action: PayloadAction<TDealPipeline>) {
            const pipeline = action.payload;
            state.entities[pipeline.id] = pipeline;
            state.ids.push(pipeline.id);
        },

        pipelineUpdated(state, action: PayloadAction<TDealPipeline>) {
            const pipeline = action.payload;
            if (state.entities[pipeline.id]) {
                // Merge the updated data with existing pipeline to preserve status array
                state.entities[pipeline.id] = {
                    ...state.entities[pipeline.id],
                    ...pipeline,
                };
            }
        },

        pipelineDeleted(state, action: PayloadAction<number>) {
            const pipelineId = action.payload;
            delete state.entities[pipelineId];
            state.ids = state.ids.filter((id) => id !== pipelineId);

            // If we deleted the current pipeline, switch to the first available one
            if (state.currentPipelineId === pipelineId && state.ids.length > 0) {
                state.currentPipelineId = state.ids[0];
            }
        },

        resetPipelines() {
            return initialState;
        },
    },
});

export const {
    startLoading,
    pipelinesLoaded,
    setCurrentPipeline,
    dealStatusCreated,
    dealStatusUpdated,
    dealStatusDeleted,
    pipelineAdded,
    pipelineUpdated,
    pipelineDeleted,
    resetPipelines,
} = pipelinesSlice.actions;

export default pipelinesSlice.reducer;
